import { ReplaySubject, catchError, forkJoin, map, Observable, of, shareReplay, finalize, Subject } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { TranslationLoader } from "./TranslationLoader";
import { mergeDeep } from "@ngx-translate/core";

export class TranslationHttpLoader implements TranslationLoader {
  private loadedKeys$ = new ReplaySubject<string>();
  // Should run when all translation loaded once
  private onResourcesLoaded$ = new Subject<void>();
  private resources = new Set<string>();
  private loading = false;
  protected loaded: Record<string, object> = {};

  get onResourcesLoaded() {
    if (this.loading) {
      return this.onResourcesLoaded$;
    }
    return of(void 0);
  }

  constructor(
    protected http: HttpClient,
    protected prefix = "json"
  ) {}

  /**
   * Set the list of resources
   * @param resources
   */
  resetResources(resources: string | string[] | Set<string>) {
    const newResources = resources instanceof Set ? resources : new Set(Array.isArray(resources) ? resources : [resources]);
    return (this.resources = newResources);
  }

  /**
   * Merge the list of resources
   * @param resources
   */
  mergeResources(resources: string | string[] | Set<string>) {
    const newResources = resources instanceof Set || Array.isArray(resources) ? resources : [resources];
    const mergedResources = [...this.resources, ...newResources];
    return this.resetResources(mergedResources);
  }

  /**
   * Get the translation for a specific language
   * @param lang
   */
  getTranslation(lang: string): Observable<Record<string, object>> {
    this.loading = true;
    return forkJoin(Array.from(this.resources).map(resource => this.prepareResource(resource, lang))).pipe(
      // Combine the translations into a single object
      map(trans => {
        return trans.reduce<Record<string, object>>((res, trans) => mergeDeep(res, trans), {});
      }),
      finalize(() => {
        this.resources.forEach(key => this.loadedKeys$.next(key));
        this.onResourcesLoaded$.next();
        this.loading = false;
      })
    );
  }

  getLoadedKeys() {
    return this.loadedKeys$.asObservable();
  }

  /**
   * Prepare resource for translation
   * @param resource
   * @param lang
   * @protected
   */
  protected prepareResource(resource: string, lang: string): Observable<object> {
    const key = `${resource}${lang}`;

    // Check if the resource is already loaded
    if (this.isResourceLoaded(key)) {
      return this.prepareLoadedResource(key);
    }

    // Load the resource for the first time
    return this.prepareNewResource(key);
  }

  /**
   * Prepare a resource that was not loaded yet.
   * @param key
   * @protected
   */
  protected prepareNewResource(key: string) {
    return this.createLoadRequest(key).pipe(
      // Add the loaded resource to the cache
      map((item: object) => this.addResource(key, item))
    );
  }

  /**
   * Prepare a resource that is already loaded
   * @param key
   * @protected
   */
  protected prepareLoadedResource(key: string): Observable<object> {
    const cachedResource = this.getResource(key);
    if (cachedResource instanceof Observable) {
      return cachedResource;
    }
    return of(cachedResource);
  }

  /**
   * Create a request to load the resource
   * @param key
   * @protected
   */
  protected createLoadRequest(key: string): Observable<object> {
    return this.http.get(`${key}.${this.prefix}`, { headers: new HttpHeaders({ "X-Disable-Toast": "true" }) }).pipe(
      // Handle errors by returning an empty object
      catchError((e: unknown) => {
        console.error(e);
        return of({});
      }),
      // Share the loaded resource to avoid making multiple requests
      shareReplay(1)
    );
  }

  /**
   * Add a resource to the cache
   * @param key
   * @param resource
   * @protected
   */
  protected addResource(key: string, resource: object): object {
    return (this.loaded[key] = resource);
  }

  /**
   * Check if a resource is already loaded
   * @param key
   * @protected
   */
  protected isResourceLoaded(key: string): boolean {
    return Boolean(this.loaded[key]);
  }

  /**
   * Get a resource from the cache
   * @param key
   * @protected
   */
  protected getResource(key: string): object {
    return this.loaded[key] ?? null;
  }
}
